import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db } from './firebase';
import { TIER_LIMITS, type SubscriptionTier } from '../constants/tiers';
import type { UserSubscription, CheckoutParams } from '../types/subscription';

// Initialize Firebase Functions lazily
let _functions: ReturnType<typeof import('firebase/functions').getFunctions> | null = null;
async function getFunctionsInstance() {
  if (!_functions) {
    const { getFunctions } = await import('firebase/functions');
    const { app } = await import('./firebase');
    _functions = getFunctions(app);
  }
  return _functions;
}

const DEFAULT_SUBSCRIPTION: UserSubscription = {
  tier: 'free',
  status: 'active',
  lemonSqueezyCustomerId: null,
  lemonSqueezySubscriptionId: null,
  lemonSqueezyOrderId: null,
  currentPeriodStart: null,
  currentPeriodEnd: null,
  cancelAtPeriodEnd: false,
  uploadsUsedThisPeriod: 0,
  uploadsIncluded: 1,
  tutorQueriesUsedThisPeriod: 0,
  tutorQueriesIncluded: 3,
  meteredSubscriptionItemId: null,
  customerPortalUrl: null,
  updatePaymentMethodUrl: null,
  createdAt: new Date().toISOString(),
  upgradedAt: null,
  cancelledAt: null,
};

export async function getSubscription(userId: string): Promise<UserSubscription> {
  const userDoc = await getDoc(doc(db, 'users', userId));
  if (!userDoc.exists()) return DEFAULT_SUBSCRIPTION;

  const data = userDoc.data();
  const tier: SubscriptionTier = data.subscription ?? 'free';
  const subData = data.subscriptionData ?? {};

  return {
    ...DEFAULT_SUBSCRIPTION,
    tier,
    ...subData,
    // Coach entitlement is read from either top-level user-doc fields (Teams
    // seat assignment) or subscriptionData (admin grants store addOns there).
    // Prefer top-level, fall back to subData so both paths surface Coach.
    addOns: data.addOns ?? subData.addOns ?? undefined,
    teamCoachAssignment: data.teamCoachAssignment ?? subData.teamCoachAssignment ?? null,
    uploadsIncluded: TIER_LIMITS[tier].uploads.included,
    tutorQueriesIncluded: TIER_LIMITS[tier].tutorQueries.included,
  };
}

export async function startCheckout(params: CheckoutParams): Promise<{ checkoutUrl: string }> {
  const functions = await getFunctionsInstance();
  const fn = httpsCallable<{ tier: string }, { checkoutUrl: string }>(functions, 'createCheckout');
  const result = await fn({ tier: params.tier });
  return result.data;
}

export async function getCustomerPortalUrl(_userId: string): Promise<{ url: string }> {
  const functions = await getFunctionsInstance();
  const fn = httpsCallable<Record<string, never>, { url: string }>(functions, 'getCustomerPortal');
  const result = await fn({});
  return result.data;
}

export async function handleCheckoutReturn(userId: string): Promise<UserSubscription> {
  // After checkout, poll Firestore until the webhook has updated the user
  // The webhook fires asynchronously, so there may be a brief delay
  return new Promise((resolve) => {
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      const sub = await getSubscription(userId);
      if (sub.tier !== 'free' || attempts >= 15) {
        clearInterval(interval);
        resolve(sub);
      }
    }, 2000);
  });
}

export function canUpload(subscription: UserSubscription): {
  allowed: boolean;
  reason: string | null;
  isOverage: boolean;
  overagePrice: number | null;
} {
  const limits = TIER_LIMITS[subscription.tier];

  if (subscription.tier === 'free') {
    // Free tier: hard limit
    if (subscription.uploadsUsedThisPeriod >= limits.uploads.included) {
      return {
        allowed: false,
        reason: 'You\'ve used your free upload. Upgrade to upload more content.',
        isOverage: false,
        overagePrice: null,
      };
    }
    return { allowed: true, reason: null, isOverage: false, overagePrice: null };
  }

  // Paid tiers: always allowed, but may be overage
  const isOverage = subscription.uploadsUsedThisPeriod >= limits.uploads.included;
  return {
    allowed: true,
    reason: isOverage
      ? `This upload will be billed at $${limits.uploads.additionalPrice} on your next invoice.`
      : null,
    isOverage,
    overagePrice: isOverage ? limits.uploads.additionalPrice : null,
  };
}

export function canUseTutor(subscription: UserSubscription): {
  allowed: boolean;
  remaining: number;
  reason: string | null;
} {
  const limits = TIER_LIMITS[subscription.tier];
  const included = limits.tutorQueries.included;

  // Unlimited
  if (included === Infinity) {
    return { allowed: true, remaining: Infinity, reason: null };
  }

  const used = subscription.tutorQueriesUsedThisPeriod;
  const remaining = Math.max(0, included - used);

  if (remaining <= 0) {
    const period = limits.tutorQueries.period;
    return {
      allowed: false,
      remaining: 0,
      reason: subscription.tier === 'free'
        ? 'You\'ve used all 3 free tutor queries. Upgrade to ask more.'
        : `You've used all ${included} tutor queries this ${period}. Upgrade to Pro for unlimited queries.`,
    };
  }

  return { allowed: true, remaining, reason: null };
}

export function onSubscriptionChange(
  userId: string,
  callback: (sub: UserSubscription) => void,
): () => void {
  return onSnapshot(doc(db, 'users', userId), (snapshot) => {
    if (!snapshot.exists()) return;
    const data = snapshot.data();
    const tier: SubscriptionTier = data.subscription ?? 'free';
    const subData = data.subscriptionData ?? {};

    callback({
      ...DEFAULT_SUBSCRIPTION,
      tier,
      ...subData,
      addOns: data.addOns ?? subData.addOns ?? undefined,
      teamCoachAssignment: data.teamCoachAssignment ?? subData.teamCoachAssignment ?? null,
      uploadsIncluded: TIER_LIMITS[tier].uploads.included,
      tutorQueriesIncluded: TIER_LIMITS[tier].tutorQueries.included,
    });
  });
}
